/**
 * Minimal JWT expiry inspection — enough to decide whether to force a token
 * refresh, not a verifier (the backend verifies signatures).
 */

function jwtExpSeconds(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True when the token is already expired or within `skewSeconds` of expiry.
 *  `getIdToken` uses this to force a fresh mint (`skipCache`) proactively, so
 *  neither a first call nor a mutation retry ever sends an about-to-expire
 *  bearer that the server rejects with 401 (INP-502). Undecodable input
 *  returns false: we can't prove it's expiring, and forcing a network refresh
 *  on every call would add latency to every request. */
export function isJwtExpiringSoon(
  token: string,
  skewSeconds = 30,
  now: number = Date.now(),
): boolean {
  const exp = jwtExpSeconds(token);
  if (exp === null) return false;
  return exp * 1000 - now <= skewSeconds * 1000;
}
